import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Empty, ErrorBanner, Panel, Spinner } from '../lib/ui';

/**
 * Per-market notification contacts.
 *
 * Completion emails used to go to one company-wide list, which stops working
 * the moment there is more than one market: the person who looks after Houston
 * has no use for a completion in Phoenix, and an alert everyone learns to
 * ignore is worse than no alert.
 *
 * Recipients resolve through the *unit's* market — "who looks after this
 * board" — so this screen sits beside Units and Installers rather than in a
 * settings drawer.
 */

interface Contact {
  id: string;
  marketId: string;
  email: string;
  name: string | null;
  isActive: boolean;
  notifyOnCompletion: boolean;
}

interface MarketWithContacts {
  id: string;
  code: string;
  name: string;
  dma: string | null;
  state: string | null;
  contacts: Contact[];
}

export function RegionsPage() {
  const [markets, setMarkets] = useState<MarketWithContacts[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, { email: string; name: string }>>({});

  const load = useCallback(() => {
    api
      .get<{ markets: MarketWithContacts[] }>('/api/admin/settings/market-contacts')
      .then((d) => {
        setMarkets(d.markets);
        setError(null);
      })
      .catch((e: unknown) => setError(e));
  }, []);

  useEffect(load, [load]);

  function draftFor(marketId: string): { email: string; name: string } {
    return draft[marketId] ?? { email: '', name: '' };
  }

  async function addContact(marketId: string): Promise<void> {
    const d = draftFor(marketId);
    if (d.email.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/settings/markets/${marketId}/contacts`, {
        email: d.email.trim(),
        name: d.name.trim() === '' ? null : d.name.trim(),
      });
      setDraft((prev) => ({ ...prev, [marketId]: { email: '', name: '' } }));
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(contact: Contact): Promise<void> {
    setBusy(true);
    try {
      await api.patch(`/api/admin/settings/market-contacts/${contact.id}`, {
        isActive: !contact.isActive,
      });
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(contact: Contact): Promise<void> {
    if (!window.confirm(`Remove ${contact.email} from this region's notifications?`)) return;
    setBusy(true);
    try {
      await fetch(`/api/admin/settings/market-contacts/${contact.id}`, { method: 'DELETE' });
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const withoutContacts = (markets ?? []).filter(
    (m) => m.contacts.filter((c) => c.isActive).length === 0,
  ).length;

  return (
    <div className="space-y-4">
      {error !== null && <ErrorBanner error={error} />}

      <Panel title="Completion notifications by region">
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          When a crew marks a job complete, the people listed for that board's region are emailed.
          A region with nobody listed falls back to the company-wide list, so a completion is never
          silent — but the fallback goes to everyone, which is what this screen exists to avoid.
        </p>
        {withoutContacts > 0 && markets !== null && (
          <p className="mt-2 border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            {withoutContacts} of {markets.length} regions have no active contact and will use the
            fallback list.
          </p>
        )}
      </Panel>

      {markets === null ? (
        <Spinner />
      ) : markets.length === 0 ? (
        <Empty>No regions yet.</Empty>
      ) : (
        markets.map((m) => {
          const d = draftFor(m.id);
          return (
            <Panel
              key={m.id}
              title={`${m.name} (${m.code})`}
              actions={
                <span className="text-xs text-neutral-500">
                  {m.contacts.filter((c) => c.isActive).length || 'no'} contact
                  {m.contacts.filter((c) => c.isActive).length === 1 ? '' : 's'}
                </span>
              }
            >
              {m.contacts.length === 0 ? (
                <Empty>Nobody listed — completions here go to the fallback list.</Empty>
              ) : (
                <ul className="mb-3 space-y-1">
                  {m.contacts.map((c) => (
                    <li
                      key={c.id}
                      className={`flex flex-wrap items-center gap-2 text-xs ${
                        c.isActive ? '' : 'opacity-50'
                      }`}
                    >
                      <span className="font-medium">{c.email}</span>
                      {c.name !== null && <span className="text-neutral-500">{c.name}</span>}
                      {!c.isActive && <span className="text-neutral-500">(paused)</span>}
                      <button
                        onClick={() => void toggle(c)}
                        disabled={busy}
                        className="ml-auto border border-neutral-300 px-1.5 py-0.5 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
                      >
                        {c.isActive ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        onClick={() => void remove(c)}
                        disabled={busy}
                        className="border border-red-300 px-1.5 py-0.5 text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void addContact(m.id);
                }}
                className="flex flex-wrap items-center gap-2"
              >
                <input
                  type="email"
                  value={d.email}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [m.id]: { ...d, email: e.target.value } }))
                  }
                  placeholder="name@harkeymedia.com"
                  className="w-56 border border-neutral-300 px-1.5 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-950"
                  aria-label={`Email to notify for ${m.name}`}
                />
                <input
                  value={d.name}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [m.id]: { ...d, name: e.target.value } }))
                  }
                  placeholder="Name (optional)"
                  className="w-40 border border-neutral-300 px-1.5 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-950"
                  aria-label={`Contact name for ${m.name}`}
                />
                <button
                  type="submit"
                  disabled={busy || d.email.trim() === ''}
                  className="bg-harkey-gold px-2 py-0.5 text-xs font-semibold text-black disabled:opacity-50"
                >
                  Add
                </button>
              </form>
            </Panel>
          );
        })
      )}
    </div>
  );
}
